import { IActivityHistoryGroup, IActivityHistoryItem, IHistoryDataImporterRules } from "../domain/model";

export interface ImporterViewFilters {
  todayOnly: boolean;
  /** Codes (codi) of the diagnostics the user has selected in the dropdown.
   * Empty means: do not filter by diagnostics. */
  selectedDiagnosticCodes: string[];
}

export type RulesByCollectionId = Record<string, IHistoryDataImporterRules>;

const isToday = (dateStr: string): boolean => {
  const d = new Date(dateStr);
  const now = new Date();
  return d.toDateString() === now.toDateString();
};

const itemMatchesToday = (item: IActivityHistoryItem | undefined): boolean => Boolean(item && isToday(item.date));

const itemHasAnyDiagnostic = (item: IActivityHistoryItem, codes: string[]): boolean =>
  Boolean(item.diagnostics?.some((d) => codes.includes(d.codi)));

/**
 * Per-collection allowlist check. Items from a collection without a rule
 * (or whose rule has no `allowedTagIds`) always pass. Items from a
 * collection with a rule must have a `tag.id` in the allowlist.
 */
const isAllowedItem = (item: IActivityHistoryItem, rules: RulesByCollectionId): boolean => {
  const collectionRule = item.type ? rules[item.type] : undefined;
  if (!collectionRule?.allowedTagIds || collectionRule.allowedTagIds.length === 0) return true;
  return collectionRule.allowedTagIds.includes(item.tag?.id ?? "");
};

const groupMatchesToday = (group: IActivityHistoryGroup): boolean => {
  const firstItem = group.items?.[0] || group.subGroups?.[0]?.items?.[0];
  return itemMatchesToday(firstItem);
};

const filterGroupItemsByPredicate = (
  group: IActivityHistoryGroup,
  predicate: (item: IActivityHistoryItem) => boolean,
): IActivityHistoryGroup => ({
  ...group,
  items: (group.items || []).filter(predicate),
  subGroups: (group.subGroups || [])
    .map((sub) => ({ ...sub, items: sub.items.filter(predicate) }))
    .filter((sub) => sub.items.length > 0),
});

const filterGroupByDiagnostics = (group: IActivityHistoryGroup, codes: string[]): IActivityHistoryGroup =>
  filterGroupItemsByPredicate(group, (it) => itemHasAnyDiagnostic(it, codes));

const isGroupNonEmpty = (group: IActivityHistoryGroup): boolean =>
  group.items.length > 0 || group.subGroups.length > 0;

/**
 * Applies importer-only view filters on top of the already-filtered groups
 * returned by activityHistoryGroupedItems. Filters are visual: they only
 * affect what's rendered, never the underlying selection.
 *
 * `rulesByCollectionId` carries per-plugin rules (allowlist of tag ids,
 * etc.) declared at inject time. Items from a collection with no rule
 * pass through unchanged.
 *
 * The `todayOnly` and `selectedDiagnosticCodes` filters compose as a UNION
 * (OR): when both are active, a group is shown if it is from today (with
 * all its items) OR contains items matching the selected diagnostics
 * (only the matching items).
 */
export const applyImporterFilters = (
  groups: IActivityHistoryGroup[],
  { todayOnly, selectedDiagnosticCodes }: ImporterViewFilters,
  rulesByCollectionId: RulesByCollectionId = {},
): IActivityHistoryGroup[] => {
  // 1) Per-collection allowlist (always applied). Plugins without rules
  //    let all their items through.
  const baseFiltered = groups
    .map((group) => filterGroupItemsByPredicate(group, (item) => isAllowedItem(item, rulesByCollectionId)))
    .filter(isGroupNonEmpty);

  // 2) View filters from the user (today / diagnostics) compose as UNION.
  const todayActive = todayOnly;
  const diagActive = selectedDiagnosticCodes.length > 0;

  if (!todayActive && !diagActive) return baseFiltered;

  return baseFiltered
    .map((group) => {
      if (todayActive && groupMatchesToday(group)) return group;
      if (diagActive) return filterGroupByDiagnostics(group, selectedDiagnosticCodes);
      return null;
    })
    .filter((group): group is IActivityHistoryGroup => group !== null && isGroupNonEmpty(group));
};

export const flattenGroupItems = (groups: IActivityHistoryGroup[]): IActivityHistoryItem[] => {
  const all: IActivityHistoryItem[] = [];
  for (const group of groups) {
    for (const item of group.items || []) all.push(item);
    for (const sub of group.subGroups || []) {
      for (const item of sub.items) all.push(item);
    }
  }
  return all;
};
